import { useState, useEffect, useRef } from "react";
import { getToken } from "utils/auth";
import { BASE_URL } from "utils/api";


const MOBILE_BASE = `${BASE_URL}/mobile`;
const ADMIN_BASE  = BASE_URL;


const FONT = "'DM Sans',sans-serif";

const INPUT = {
  width: "100%", padding: "10px 14px", borderRadius: 9,
  border: "1.5px solid #e5e7eb", fontSize: 14, color: "#111827",
  background: "#fafafa", outline: "none", boxSizing: "border-box",
  fontFamily: FONT,
};
const LABEL = {
  fontSize: 11, fontWeight: 700, color: "#6b7280",
  marginBottom: 6, display: "block", letterSpacing: .5,
  textTransform: "uppercase",
};

const TARGET_OPTIONS = [
  { value: "all",      label: "All Customers" },
  { value: "customer", label: "Customer" },
];


function Toast({ msg, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24, zIndex: 9999,
      background: type === "success" ? "#166534" : "#991b1b",
      color: "#fff", padding: "12px 20px", borderRadius: 10,
      fontFamily: FONT, fontSize: 14, fontWeight: 500,
      boxShadow: "0 8px 32px rgba(0,0,0,.22)",
      animation: "slideIn .3s ease",
    }}>{msg}</div>
  );
}

function Badge({ children, color }) {
  const map = { green: { bg: "#dcfce7", text: "#166534" }, gray: { bg: "#f3f4f6", text: "#374151" } };
  const c = map[color] || map.gray;
  return (
    <span style={{
      background: c.bg, color: c.text, padding: "2px 10px",
      borderRadius: 99, fontSize: 11, fontWeight: 600, letterSpacing: .4,
    }}>{children}</span>
  );
}

function ImageUploadField({ label, file, preview, onChange, onClear }) {
  const inputRef = useRef(null);

  return (
    <div>
      <label style={LABEL}>{label}</label>
      <div
        onClick={() => !file && inputRef.current?.click()}
        style={{
          border: `2px dashed ${file ? "#16a34a" : "#d1d5db"}`,
          borderRadius: 10, padding: "14px 16px",
          background: file ? "#f0fdf4" : "#fafafa",
          cursor: file ? "default" : "pointer",
          display: "flex", alignItems: "center", gap: 12,
          transition: "border-color .2s, background .2s",
          minHeight: 72,
        }}
      >
        {preview ? (
          <>
            <img
              src={preview} alt={label}
              style={{ width: 52, height: 52, borderRadius: 8, objectFit: "cover", flexShrink: 0 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                margin: 0, fontSize: 13, fontWeight: 600, color: "#14532d",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{file?.name || "Uploaded image"}</p>
              {file && (
                <p style={{ margin: "2px 0 0", fontSize: 11, color: "#6b7280" }}>
                  {(file.size / 1024).toFixed(0)} KB · compressed to WebP on upload
                </p>
              )}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              style={{
                background: "#fee2e2", border: "none", borderRadius: 6,
                color: "#dc2626", fontWeight: 700, fontSize: 13,
                padding: "4px 10px", cursor: "pointer", flexShrink: 0,
              }}
            >Remove</button>
          </>
        ) : (
          <>
            <div style={{
              width: 40, height: 40, borderRadius: 8, background: "#e5e7eb",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20, flexShrink: 0,
            }}>🖼️</div>
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#374151" }}>Click to upload</p>
              <p style={{ margin: "2px 0 0", fontSize: 11, color: "#9ca3af" }}>
                JPG, PNG, GIF, WebP · max 10 MB
              </p>
            </div>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onChange(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}


function SearchDropdown({ label, placeholder, value, onChange, fetchItems, renderLabel }) {
  const [search, setSearch]     = useState("");
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [open, setOpen]         = useState(false);
  const [selected, setSelected] = useState(null);
  const wrapRef                 = useRef(null);
  const debounceRef             = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const load = async (term = "") => {
    setLoading(true);
    try { setItems(await fetchItems(term)); }
    catch { setItems([]); }
    finally { setLoading(false); }
  };

  const handleFocus  = () => { setOpen(true); if (items.length === 0) load(); };
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(val), 400);
  };
const handleSelect = (item) => {
  setSelected(item);
  onChange(item.id);
  setSearch(item.name || renderLabel(item)); 
  setOpen(false);
};
  const handleClear = () => {
    setSelected(null); onChange(""); setSearch(""); setItems([]); setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <label style={LABEL}>{label}</label>
      <div style={{ position: "relative" }}>
        <input
          style={{ ...INPUT, paddingRight: 36 }}
          placeholder={placeholder}
          value={selected ? renderLabel(selected) : search}
          onChange={selected ? undefined : handleSearch}
          onFocus={handleFocus}
          readOnly={!!selected}
        />
        {(selected || search) && (
          <button onClick={handleClear} style={{
            position: "absolute", right: 10, top: "50%",
            transform: "translateY(-50%)", background: "none",
            border: "none", cursor: "pointer", fontSize: 18,
            color: "#9ca3af", lineHeight: 1, padding: 0,
          }}>×</button>
        )}
      </div>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
          background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,.1)", zIndex: 999,
          maxHeight: 220, overflowY: "auto",
        }}>
          {loading ? (
            <div style={{ padding: "14px 16px", fontSize: 13, color: "#9ca3af", fontFamily: FONT }}>Loading...</div>
          ) : items.length === 0 ? (
            <div style={{ padding: "14px 16px", fontSize: 13, color: "#9ca3af", fontFamily: FONT }}>No results found</div>
          ) : items.map((item) => (
            <div
              key={item.id} onClick={() => handleSelect(item)}
              style={{
                padding: "10px 16px", cursor: "pointer", fontSize: 14,
                color: "#111827", fontFamily: FONT, borderBottom: "1px solid #f3f4f6",
                background: value === item.id ? "#f0fdf4" : "#fff",
                fontWeight: value === item.id ? 600 : 400,
              }}
              onMouseEnter={(e) => { if (value !== item.id) e.currentTarget.style.background = "#f9fafb"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = value === item.id ? "#f0fdf4" : "#fff"; }}
            >{renderLabel(item)}</div>
          ))}
        </div>
      )}
    </div>
  );
}


export default function PushNotifications() {
  const [sending, setSending] = useState(false);
  const [toast, setToast]     = useState(null);

  const [form, setForm] = useState({
    target: "all", customer_id: "",
    title_en: "", title_ar: "",
    description_en: "", description_ar: "",
    send_type: "now",    
  scheduled_at: "", 
  });


  const [imageEnFile, setImageEnFile] = useState(null);
  const [imageArFile, setImageArFile] = useState(null);
  const [imageEnPreview, setImageEnPreview] = useState(null);
  const [imageArPreview, setImageArPreview] = useState(null);

  const token     = getToken();
  const showToast = (msg, type = "success") => setToast({ msg, type });
  const set       = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));


  const setImage = (locale, file) => {
    if (locale === "en") {
      if (imageEnPreview) URL.revokeObjectURL(imageEnPreview);
      setImageEnFile(file);
      setImageEnPreview(file ? URL.createObjectURL(file) : null);
    } else {
      if (imageArPreview) URL.revokeObjectURL(imageArPreview);
      setImageArFile(file);
      setImageArPreview(file ? URL.createObjectURL(file) : null);
    }
  };

  const fetchCustomers = async (search = "") => {
    const url = `${ADMIN_BASE}/mobile/customer/list?limit=50${search ? `&search=${encodeURIComponent(search)}` : ""}`;
    const res  = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    if (!data.status) return [];
    return (data.data || []).map((c) => ({
  id: c.id,
  name: c.name || "Unnamed",
  mobile_number: c.mobile_number ?? "",
  label: `${c.name || "Unnamed"} - ${c.mobile_number || 'No number'}`,
  value: c.id,
}));
  };

  const handleSend = async () => {
  if (!form.title_en.trim()) {
  showToast("Title (English) is required", "error");
  return;
}
if (form.send_type === "schedule" && !form.scheduled_at) {
  showToast("Please select schedule date/time", "error");
  return;
}
      

  
    const fd = new FormData();
    fd.append("title_en", form.title_en.trim());
    if (form.title_ar.trim())       fd.append("title_ar",       form.title_ar.trim());
    if (form.description_en.trim()) fd.append("description_en", form.description_en.trim());
    if (form.description_ar.trim()) fd.append("description_ar", form.description_ar.trim());
    if (form.target === "customer" && form.customer_id)
                                    fd.append("customer_id",    form.customer_id);
    if (imageEnFile) fd.append("image_en", imageEnFile, imageEnFile.name);
    if (imageArFile) fd.append("image_ar", imageArFile, imageArFile.name);
if (form.send_type === "schedule" && form.scheduled_at) {
  fd.append("scheduled_at", new Date(form.scheduled_at).toISOString());
}
    setSending(true);
    try {
      const res = await fetch(`${MOBILE_BASE}/notification/send`, {
        method:  "POST",
       
        headers: { Authorization: `Bearer ${token}` },
        body:    fd,
      });
      const data = await res.json();

      if (res.status === 401 || res.status === 403) {
        showToast("Access denied — check your login session", "error");
        return;
      }
      if (data.status) {
        showToast(data.message || "Notification sent!");
       setForm({ target: "all", customer_id: "", title_en: "", title_ar: "", description_en: "", description_ar: "", send_type: "now", scheduled_at: "" });
        setImage("en", null);
        setImage("ar", null);
      } else {
        showToast(data.message || "Failed to send", "error");
      }
    } catch {
      showToast("Network error", "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ fontFamily: FONT, background: "#f0faf4", minHeight: "100vh", padding: "28px 32px" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
        input:focus, textarea:focus {
          border-color: #16a34a !important;
          box-shadow: 0 0 0 3px rgba(22,163,74,.12);
        }
        @keyframes slideIn {
          from { transform: translateY(16px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, background: "#16a34a",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
        }}>&#128276;</div>
        <div>
          <p style={{ fontSize: 22, fontWeight: 700, color: "#14532d", margin: 0 }}>Send Notification</p>
          <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>Push notifications to customers</p>
        </div>
      </div>

      {/* Card */}
      <div style={{
        background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb",
        boxShadow: "0 1px 8px rgba(0,0,0,.05)", padding: 28,
      }}>
        <p style={{ fontSize: 15, fontWeight: 700, color: "#14532d", margin: "0 0 20px" }}>
          Compose Notification
        </p>
        {/* Send Type */}
<div style={{ marginBottom: 20 }}>
  <span style={LABEL}>Send Type</span>

  <div style={{ display: "flex", gap: 8 }}>
    <button
      type="button"
      onClick={() =>
        setForm((f) => ({
          ...f,
          send_type: "now",
          scheduled_at: "",
        }))
      }
      style={{
        padding: "8px 16px",
        borderRadius: 8,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 600,
        border: `1.5px solid ${
          form.send_type === "now" ? "#16a34a" : "#e5e7eb"
        }`,
        background:
          form.send_type === "now" ? "#dcfce7" : "#fff",
        color:
          form.send_type === "now" ? "#14532d" : "#6b7280",
      }}
    >
      ⚡ Send Now
    </button>

    <button
      type="button"
      onClick={() =>
        setForm((f) => ({
          ...f,
          send_type: "schedule",
        }))
      }
      style={{
        padding: "8px 16px",
        borderRadius: 8,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 600,
        border: `1.5px solid ${
          form.send_type === "schedule"
            ? "#16a34a"
            : "#e5e7eb"
        }`,
        background:
          form.send_type === "schedule"
            ? "#dcfce7"
            : "#fff",
        color:
          form.send_type === "schedule"
            ? "#14532d"
            : "#6b7280",
      }}
    >
      🗓 Schedule
    </button>
  </div>
</div>
{form.send_type === "schedule" && (
  <div style={{ marginBottom: 20 }}>
    <label style={LABEL}>Schedule Date & Time</label>

    <input
      type="datetime-local"
      style={INPUT}
      min={new Date(Date.now() + 60000)
        .toISOString()
        .slice(0, 16)}
      value={form.scheduled_at}
      onChange={(e) =>
        setForm((f) => ({
          ...f,
          scheduled_at: e.target.value,
        }))
      }
    />
  </div>
)}

        {/* Target selector */}
        <div style={{ marginBottom: 20 }}>
          <span style={LABEL}>Send To</span>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {TARGET_OPTIONS.map((o) => (
              <button
                key={o.value}
                onClick={() => setForm((f) => ({ ...f, target: o.value, customer_id: "" }))}
                style={{
                  padding: "8px 16px", borderRadius: 8, cursor: "pointer",
                  fontSize: 13, fontWeight: 600, transition: "all .2s", fontFamily: FONT,
                  border:    `1.5px solid ${form.target === o.value ? "#16a34a" : "#e5e7eb"}`,
                  background: form.target === o.value ? "#dcfce7" : "#fff",
                  color:      form.target === o.value ? "#14532d" : "#6b7280",
                }}
              >{o.label}</button>
            ))}
          </div>
        </div>

      {/* Customer dropdown */}
{form.target === "customer" && (
  <div style={{ marginBottom: 16 }}>
    <SearchDropdown
      label="Customer"
      placeholder="Search customer by name or mobile..."
      value={form.customer_id}
      onChange={(id) => setForm((f) => ({ ...f, customer_id: id }))}
      fetchItems={fetchCustomers}
      renderLabel={(item) => (
        <div style={{ lineHeight: 1.4 }}>
          <div>{item.name}</div>
          {item.mobile_number && (
            <small style={{ color: '#555' }}>
               {item.mobile_number}
            </small>
          )}
        </div>
      )}
    />
  </div>
)}

        {/* Titles */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div>
            <label style={LABEL}>Title English *</label>
            <input style={INPUT} placeholder="e.g. Weekend Sale!" value={form.title_en} onChange={set("title_en")} />
          </div>
          <div>
            <label style={LABEL}>Title Arabic</label>
            <input style={INPUT} placeholder="عنوان الإشعار" dir="rtl" value={form.title_ar} onChange={set("title_ar")} />
          </div>
        </div>

        {/* Descriptions */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div>
            <label style={LABEL}>Message English</label>
            <textarea
              style={{ ...INPUT, resize: "vertical", minHeight: 80 }}
              placeholder="Notification body..."
              value={form.description_en}
              onChange={set("description_en")}
            />
          </div>
          <div>
            <label style={LABEL}>Message Arabic</label>
            <textarea
              style={{ ...INPUT, resize: "vertical", minHeight: 80 }}
              placeholder="نص الإشعار..." dir="rtl"
              value={form.description_ar}
              onChange={set("description_ar")}
            />
          </div>
        </div>

        {/* Image file uploads */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
          <ImageUploadField
            label="Image (English)"
            file={imageEnFile}
            preview={imageEnPreview}
            onChange={(f) => setImage("en", f)}
            onClear={() => setImage("en", null)}
          />
          <ImageUploadField
            label="Image (Arabic)"
            file={imageArFile}
            preview={imageArPreview}
            onChange={(f) => setImage("ar", f)}
            onClear={() => setImage("ar", null)}
          />
        </div>

        {/* Live preview pill */}
        {form.title_en && (
          <div style={{
            marginBottom: 20, padding: "14px 18px", borderRadius: 12,
            background: "#f0fdf4", border: "1px solid #bbf7d0",
            display: "flex", gap: 12, alignItems: "center",
          }}>
            {imageEnPreview ? (
              <img src={imageEnPreview} alt=""
                style={{ width: 40, height: 40, borderRadius: 10, objectFit: "cover" }} />
            ) : (
              <div style={{
                width: 40, height: 40, borderRadius: 10, background: "#16a34a",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
              }}>&#128276;</div>
            )}
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#14532d" }}>{form.title_en}</p>
              {form.description_en && (
                <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280" }}>{form.description_en}</p>
              )}
            </div>
            <Badge color="green">Preview</Badge>
          </div>
        )}

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={sending}
          style={{
            background: sending ? "#86efac" : "#16a34a",
            color: "#fff", border: "none", padding: "12px 32px",
            borderRadius: 10, fontSize: 14, fontWeight: 700,
            cursor: sending ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 8,
            transition: "background .2s", fontFamily: FONT,
          }}
        >{sending ? "Sending..." : "Send Notification"}</button>
      </div>

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}